import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import SimplePaginator from "./SimplePaginator";
import BoardToolbar from "./info-board/BoardToolbar";
import { getBoardPosts } from "../../services/contactCenterService";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { getLocalizedField } from "../../helpers/localizedHelper";
import { useTranslation } from "react-i18next";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.COMMON.PINNED": "Pinned",
  "CONTACT_US_CENTER.COMMON.TOTAL": "{{count}} total",
  "CONTACT_US_CENTER.COMMON.VIEW": "View",
  "CONTACT_US_CENTER.COMMON.VIEW_LIST": "List view",
  "CONTACT_US_CENTER.COMMON.VIEW_GALLERY": "Gallery view",
  "CONTACT_US_CENTER.LIST.COL_NO": "NO",
  "CONTACT_US_CENTER.LIST.COL_TITLE": "Title",
  "CONTACT_US_CENTER.LIST.COL_DATE": "Date",
  "CONTACT_US_CENTER.LIST.COL_VIEWS": "Views",
  "CONTACT_US_CENTER.LIST.COL_ACTION": "Action",
  "CONTACT_US_CENTER.LIST.DOWNLOAD": "Download",
  "CONTACT_US_CENTER.LIST.EMPTY": "No posts yet.",
  "CONTACT_US_CENTER.NOTICE.TARGET_BANNER":
    "Only target notices filtered on the server for your current permissions (student, specific class, etc.) are shown.",
};

// Page sizes per view — the grid fits 8 cards nicely, the table 10 rows.
const ROWS_BY_VIEW = { gallery: 8, list: 10 };

/**
 * Posts in a board tab, rendered as either a thumbnail card grid (gallery) or a table (list).
 * When `allowViewToggle` is set, a header toggle lets the user switch views; the initial view
 * comes from `defaultView` (derived from the tab's configured viewType — POST_GALLERY -> gallery,
 * otherwise list). With the toggle off (Customer Center) the tab renders list-only.
 *
 * List-mode options:
 * - trailing="views"  : show the 조회 (view count) column (공지사항).
 * - trailing="action" : show a 액션 column with a download affordance (자료실 / 보도자료).
 * showTargetBanner adds the "server-filtered target notices" notice (공지사항).
 *
 * Opening a post is delegated to `onOpenPost(publicId)`: the /contact-us root turns it into a
 * standalone detail page (?postId=<publicId>), so no detail is rendered inline here.
 */
const BoardPostsTab = ({
  group,
  tab,
  defaultView = "list",
  trailing = "views",
  showTargetBanner = false,
  allowViewToggle = true,
  onOpenPost,
}) => {
  const [view, setView] = useState(defaultView);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const language = getI18nLanguageFromLocalStorage();
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  // Reset to the tab's default view when the active tab changes.
  useEffect(() => {
    setView(defaultView);
  }, [defaultView, tab.id]);

  const rows = ROWS_BY_VIEW[view] || 10;

  useEffect(() => {
    let alive = true;
    getBoardPosts(group, tab.id, {
      first: (page - 1) * rows,
      rows,
      search,
    }).then((res) => {
      if (!alive) return;
      setPosts(res.data || []);
      setTotal(res.pagingInfo?.totalRecordCount || 0);
    });
    return () => {
      alive = false;
    };
  }, [group, tab.id, page, rows, search]);

  const onSearch = ({ keyword }) => {
    setPage(1);
    setSearch(keyword || "");
  };

  // Switching views changes the page size, so go back to the first page.
  const onSwitchView = (next) => {
    if (next === view) return;
    setView(next);
    setPage(1);
  };

  const renderGallery = () => (
    <div className="cuc-card-grid">
      {posts.map((p) => (
        <div className="cuc-card" key={p.id} onClick={() => onOpenPost(p.publicId)}>
          <div className="cuc-card-thumb">
            {p.isPinned && (
              <span className="cuc-pin-badge cuc-card-pin">📌 {t("CONTACT_US_CENTER.COMMON.PINNED")}</span>
            )}
            {p.thumbnailUrl ? (
              <img src={p.thumbnailUrl} alt={getLocalizedField(p, "title", language)} />
            ) : (
              <div className="cuc-card-thumb-empty" />
            )}
          </div>
          <div className="cuc-card-body">
            <div className="cuc-card-title">
              {getLocalizedField(p, "title", language)}
            </div>
            <div className="cuc-card-meta">
              <span role="img" aria-label="author">🌰</span>
              <span>{(p.createdTime || "").slice(0, 10)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderList = () => (
    <table className="cuc-table">
      <thead>
        <tr>
          <th className="cuc-col-no">{t("CONTACT_US_CENTER.LIST.COL_NO")}</th>
          <th>{t("CONTACT_US_CENTER.LIST.COL_TITLE")}</th>
          <th className="cuc-col-date">{t("CONTACT_US_CENTER.LIST.COL_DATE")}</th>
          <th className={trailing === "views" ? "cuc-col-views" : "cuc-col-action"}>
            {trailing === "views"
              ? t("CONTACT_US_CENTER.LIST.COL_VIEWS")
              : t("CONTACT_US_CENTER.LIST.COL_ACTION")}
          </th>
        </tr>
      </thead>
      <tbody>
        {posts.map((r, i) => (
          <tr key={r.id}>
            <td className="cuc-col-no">
              {r.isPinned ? (
                <span className="cuc-pin-badge">📌 {t("CONTACT_US_CENTER.COMMON.PINNED")}</span>
              ) : (
                (page - 1) * rows + i + 1
              )}
            </td>
            <td>
              <span
                className={`cuc-row-title cuc-row-link ${r.isPinned ? "is-pinned" : ""}`}
                onClick={() => onOpenPost(r.publicId)}
              >
                {getLocalizedField(r, "title", language)}
              </span>
            </td>
            <td className="cuc-col-date">{(r.createdTime || "").slice(0, 10)}</td>
            {trailing === "views" ? (
              <td className="cuc-col-views">{r.viewCount}</td>
            ) : (
              <td className="cuc-col-action">
                {r.attachmentCount > 0 ? (
                  <Button
                    link
                    icon="pi pi-download"
                    label={t("CONTACT_US_CENTER.LIST.DOWNLOAD")}
                    onClick={() => onOpenPost(r.publicId)}
                  />
                ) : (
                  "-"
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div>
      {showTargetBanner && (
        <div className="cuc-info-banner">
          <span role="img" aria-label="info">ℹ️</span>
          <span>{t("CONTACT_US_CENTER.NOTICE.TARGET_BANNER")}</span>
        </div>
      )}

      <BoardToolbar onSearch={onSearch} />

      <div className="cuc-board-count-row">
        <div className="cuc-board-count">
          {t("CONTACT_US_CENTER.COMMON.TOTAL", { count: total })}
        </div>
        {allowViewToggle && (
          <div className="cuc-view-toggle" role="group" aria-label={t("CONTACT_US_CENTER.COMMON.VIEW")}>
            <button
              type="button"
              className={`cuc-view-btn ${view === "list" ? "is-active" : ""}`}
              onClick={() => onSwitchView("list")}
              aria-pressed={view === "list"}
              aria-label={t("CONTACT_US_CENTER.COMMON.VIEW_LIST")}
              title={t("CONTACT_US_CENTER.COMMON.VIEW_LIST")}
            >
              <i className="pi pi-list" />
            </button>
            <button
              type="button"
              className={`cuc-view-btn ${view === "gallery" ? "is-active" : ""}`}
              onClick={() => onSwitchView("gallery")}
              aria-pressed={view === "gallery"}
              aria-label={t("CONTACT_US_CENTER.COMMON.VIEW_GALLERY")}
              title={t("CONTACT_US_CENTER.COMMON.VIEW_GALLERY")}
            >
              <i className="pi pi-th-large" />
            </button>
          </div>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="cuc-empty">{t("CONTACT_US_CENTER.LIST.EMPTY")}</div>
      ) : view === "gallery" ? (
        renderGallery()
      ) : (
        renderList()
      )}

      <SimplePaginator
        page={page}
        pageCount={Math.ceil(total / rows)}
        onChange={setPage}
      />
    </div>
  );
};

export default BoardPostsTab;
